const COUNTRY_LABELS = {
  rks: 'Kosovo',
  alb: 'Albania',
  nmk: 'North Macedonia'
}

const PAYROLL_COUNTRY_META = {
  rks: {
    currencyCode: 'EUR',
    currencySymbol: '€',
    currencyLabel: 'EUR',
    payrollExecutionSupported: true,
    secondJobSupported: true,
    unsupportedExecutionMessage: '',
    unsupportedExecutionMessageKey: ''
  },
  alb: {
    currencyCode: 'ALL',
    currencySymbol: 'LEK',
    currencyLabel: 'LEK',
    payrollExecutionSupported: true,
    secondJobSupported: true,
    unsupportedExecutionMessage: '',
    unsupportedExecutionMessageKey: ''
  },
  nmk: {
    currencyCode: 'MKD',
    currencySymbol: 'MKD',
    currencyLabel: 'MKD',
    payrollExecutionSupported: false,
    secondJobSupported: false,
    unsupportedExecutionMessage:
      'Payroll is not supported for North Macedonia companies.',
    unsupportedExecutionMessageKey: 'inputError.payroll_not_supported_nmk'
  }
}

const COUNTRY_CURRENCY_DEFAULTS = {
  rks: 'EUR',
  alb: 'LEK',
  nmk: 'MKD'
}

const COUNTRY_CURRENCY_OPTIONS = {
  rks: ['EUR'],
  alb: ['LEK', 'EUR'],
  nmk: ['MKD']
}

const CURRENCY_META = {
  EUR: {
    code: 'EUR',
    symbol: '€',
    label: 'EUR'
  },
  LEK: {
    code: 'LEK',
    symbol: 'LEK',
    label: 'LEK'
  },
  MKD: {
    code: 'MKD',
    symbol: 'MKD',
    label: 'MKD'
  }
}

const PAYROLL_FIELD_LABELS = {
  default: {
    contribution_5_percent: 'Pension Contribution (5%)',
    tax_0_to_80_0_percent: 'Tax 0-80 (0%)',
    tax_80_to_250_4_percent: 'Tax 80-250 (4%)',
    tax_250_to_450_8_percent: 'Tax 250-450 (8%)',
    tax_greater_450_10_percent: 'Tax >450 (10%)'
  },
  alb: {
    contribution_5_percent: 'Employee Social + Health Contributions',
    tax_greater_450_10_percent: 'Income Tax'
  }
}

const PAYROLL_FIELD_VISIBILITY = {
  default: [
    'contribution_5_percent',
    'tax_0_to_80_0_percent',
    'tax_80_to_250_4_percent',
    'tax_250_to_450_8_percent',
    'tax_greater_450_10_percent'
  ],
  alb: ['contribution_5_percent', 'tax_greater_450_10_percent'],
  nmk: []
}

const SALARY_VALIDATION = {
  default: {
    min: null,
    max: null,
    contributionBaseMin: null,
    contributionBaseMax: null,
    currency: 'EUR'
  },
  alb: {
    min: null,
    max: null,
    contributionBaseMin: 40000,
    contributionBaseMax: 186416,
    currency: 'LEK'
  },
  nmk: {
    min: null,
    max: null,
    contributionBaseMin: null,
    contributionBaseMax: null,
    currency: 'MKD'
  }
}

const SALARY_VALIDATION_BY_CURRENCY = {
  LEK: SALARY_VALIDATION.alb,
  MKD: SALARY_VALIDATION.nmk,
  EUR: SALARY_VALIDATION.default
}

function getPayrollCountryMeta(country) {
  const normalized = normalizeCountryCode(country)
  return PAYROLL_COUNTRY_META[normalized] || PAYROLL_COUNTRY_META.rks
}

function roundNumberToScale(num, scale) {
  if (!('' + num).includes('e')) {
    return +(Math.round(num + 'e+' + scale) + 'e-' + scale)
  }

  const arr = ('' + num).split('e')
  let sign = ''

  if (+arr[1] + scale > 0) {
    sign = '+'
  }

  return +(Math.round(+arr[0] + 'e' + sign + (+arr[1] + scale)) + 'e-' + scale)
}

export function normalizeCurrencyCode(currency) {
  const normalized = String(currency || '')
    .trim()
    .toUpperCase()

  return normalized || null
}

export function normalizeCountryCode(country) {
  if (!country) return 'rks'

  const rawCountry =
    typeof country === 'object'
      ? country.value || country.country_value
      : country

  const normalized = String(rawCountry || '')
    .trim()
    .toLowerCase()

  if (normalized === 'kosovo') return 'rks'
  if (normalized === 'albania') return 'alb'
  if (normalized === 'north macedonia') return 'nmk'

  if (COUNTRY_LABELS[normalized]) {
    return normalized
  }

  return 'rks'
}

export function getCountryLabel(country) {
  const normalized = normalizeCountryCode(country)
  return COUNTRY_LABELS[normalized] || COUNTRY_LABELS.rks
}

export function getCountryOption(country) {
  const value = normalizeCountryCode(country)
  return {
    value,
    label: getCountryLabel(value)
  }
}

export function getCountryDisplayText(country) {
  const option = getCountryOption(country)
  return `${option.label} (${option.value})`
}

export function getPayrollCurrency(country) {
  const meta = getPayrollCountryMeta(country)
  return {
    code: meta.currencyCode,
    symbol: meta.currencySymbol,
    label: meta.currencyLabel
  }
}

export function getCurrencyOption(currency) {
  const normalizedCurrency = normalizeCurrencyCode(currency) || 'EUR'
  const meta = CURRENCY_META[normalizedCurrency] || CURRENCY_META.EUR

  return {
    value: meta.code,
    label: `${meta.label} (${meta.symbol})`
  }
}

export function getAllowedCurrencies(country) {
  const normalized = normalizeCountryCode(country)
  return COUNTRY_CURRENCY_OPTIONS[normalized] || COUNTRY_CURRENCY_OPTIONS.rks
}

export function getCurrencyOptionsForCountry(country) {
  return getAllowedCurrencies(country).map(getCurrencyOption)
}

export function getDefaultCompanyCurrency(country) {
  const normalized = normalizeCountryCode(country)
  return COUNTRY_CURRENCY_DEFAULTS[normalized] || COUNTRY_CURRENCY_DEFAULTS.rks
}

export function getDefaultEmployeeCurrency(country) {
  return getDefaultCompanyCurrency(country)
}

export function resolveEmployeeCurrency(
  country,
  companyCurrency,
  employeeCurrency
) {
  const normalizedEmployeeCurrency = normalizeCurrencyCode(employeeCurrency)
  const allowedCurrencies = getAllowedCurrencies(country)

  if (
    normalizedEmployeeCurrency &&
    allowedCurrencies.includes(normalizedEmployeeCurrency)
  ) {
    return normalizedEmployeeCurrency
  }

  return getDefaultEmployeeCurrency(country, companyCurrency)
}

export function formatCurrencySuffix(country) {
  return getPayrollCurrency(country).symbol
}

export function getCurrencyDecimalScale(currency) {
  return normalizeCurrencyCode(currency) === 'EUR' ? 2 : 0
}

export function formatPayrollAmount(amount, currency) {
  if (amount === null || amount === undefined || amount === '') {
    return ''
  }

  const parsedAmount = Number(amount)

  if (!Number.isFinite(parsedAmount)) {
    return amount
  }

  const scale = getCurrencyDecimalScale(currency)
  return roundNumberToScale(parsedAmount, scale).toFixed(scale)
}

export function getPayrollFieldLabel(country, fieldKey) {
  const normalized = normalizeCountryCode(country)
  const countryLabels =
    PAYROLL_FIELD_LABELS[normalized] || PAYROLL_FIELD_LABELS.default

  return (
    countryLabels[fieldKey] ||
    PAYROLL_FIELD_LABELS.default[fieldKey] ||
    fieldKey
  )
}

export function shouldShowPayrollField(country, fieldKey) {
  const normalized = normalizeCountryCode(country)
  const visibleFields =
    PAYROLL_FIELD_VISIBILITY[normalized] || PAYROLL_FIELD_VISIBILITY.default

  return visibleFields.includes(fieldKey)
}

export function getSalaryValidationMeta(country, currency) {
  const normalizedCurrency = normalizeCurrencyCode(currency)

  if (normalizedCurrency && SALARY_VALIDATION_BY_CURRENCY[normalizedCurrency]) {
    return SALARY_VALIDATION_BY_CURRENCY[normalizedCurrency]
  }

  const normalized = normalizeCountryCode(country)
  return SALARY_VALIDATION[normalized] || SALARY_VALIDATION.default
}

export function isSecondJobSupported(country) {
  return getPayrollCountryMeta(country).secondJobSupported
}

export function isPayrollExecutionSupported(country) {
  return getPayrollCountryMeta(country).payrollExecutionSupported
}

export function getUnsupportedPayrollExecutionMessage(country) {
  return getPayrollCountryMeta(country).unsupportedExecutionMessage
}

export function getUnsupportedPayrollExecutionMessageKey(country) {
  return getPayrollCountryMeta(country).unsupportedExecutionMessageKey
}
